import React, { useCallback, useEffect, useState } from "react";
import { Activity, AlertTriangle, Check, Hand, History, RefreshCw, Zap } from "lucide-react";
import * as api from "../../api";

const fmtDate = (iso) => iso ? new Date(iso).toLocaleString("es-VE") : "—";
const fmtBs = (v) => v == null ? "—" : `Bs ${Number(v).toLocaleString("es-VE")}`;

/** Estado y control de la actualización automática de la TASA_AG7 (fuente BCV). */
export function RateAutomationCard({ showToast, onRateChanged, activeRate }) {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    try {
      setStatus(await api.getExchangeRateStatus());
    } catch (e) {
      console.warn("rate status failed:", e);
    }
  }, []);

  useEffect(() => { load(); }, [load, activeRate]);

  const run = async (action, fn, okMsg) => {
    setBusy(action);
    try {
      const res = await fn();
      await load();
      if (res?.status === "error") showToast?.("BCV no disponible", res.error || "Reintenta en unos minutos", "error");
      else showToast?.(okMsg, res?.status === "updated" ? `Nueva tasa: ${fmtBs(res.rate)}` : "El BCV no ha cambiado", "success");
      if (res?.status === "updated") onRateChanged?.(res.rate);
    } catch (e) {
      showToast?.("Error", e?.message || "No se pudo completar la acción", "error");
    } finally {
      setBusy("");
    }
  };

  if (!status) return null;
  const manual = status.mode === "MANUAL";
  const lockedToday = status.locked_date && status.locked_date === new Date().toLocaleDateString("en-CA", { timeZone: "America/Caracas" });

  return (
    <div className="rounded-3xl bg-ag7-surface border border-[var(--ag7-border-soft)] p-8 space-y-5"
      data-testid="rate-automation-card">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-full bg-cyan-950/60 border border-cyan-500 flex items-center justify-center">
            <Activity className="w-5 h-5 text-cyan-400" />
          </div>
          <div>
            <h3 className="text-xl font-heading font-extrabold tracking-tight text-white">Actualización Automática</h3>
            <p className="text-[11px] text-zinc-400">Fuente oficial: BCV · {status.schedule?.days} desde {status.schedule?.start} ({status.schedule?.timezone}), cada {status.schedule?.interval_minutes} min.</p>
          </div>
        </div>
        <span data-testid="rate-mode-badge"
          className={`px-3 py-1.5 rounded-full text-[10px] font-mono uppercase tracking-widest border inline-flex items-center gap-1.5 ${
            manual ? "text-amber-300 border-amber-500/50 bg-amber-950/30" : "text-emerald-300 border-emerald-500/50 bg-emerald-950/30"}`}>
          {manual ? <><Hand className="w-3 h-3" /> Manual</> : <><Zap className="w-3 h-3" /> Automático</>}
        </span>
      </div>

      <div className="grid sm:grid-cols-2 gap-3 text-[11px] font-mono">
        {[
          ["TASA_AG7 vigente", fmtBs(status.exchange_rate), "rate-current"],
          ["BCV usado", status.bcv_usd != null ? `${Number(status.bcv_usd).toLocaleString("es-VE", { maximumFractionDigits: 4 })} → ${status.bcv_rounded}` : "—", "rate-bcv"],
          ["Última consulta", fmtDate(status.last_check_at), "rate-last-check"],
          ["Último cambio", fmtDate(status.last_change_at), "rate-last-change"],
        ]?.map(([label, value, tid]) => (
          <div key={tid} className="p-3 rounded-xl bg-ag7-bg border border-[var(--ag7-border-soft)]">
            <span className="text-[10px] uppercase tracking-widest text-zinc-500 block">{label}</span>
            <strong className="text-white text-sm" data-testid={tid}>{value}</strong>
          </div>
        ))}
      </div>

      {manual && (
        <p className="p-3 rounded-xl bg-amber-950/25 border border-amber-500/40 text-amber-200 text-xs" data-testid="rate-manual-note">
          La tasa está fijada manualmente: el BCV no la sobrescribirá hasta que reactives la actualización automática.
        </p>
      )}
      {!manual && lockedToday && (
        <p className="p-3 rounded-xl bg-emerald-950/25 border border-emerald-500/40 text-emerald-200 text-xs" data-testid="rate-locked-note">
          Ya se detectó el cambio del BCV de hoy. Las consultas se reanudan el próximo día hábil.
        </p>
      )}
      {status.error_count >= 2 && (
        <p className="p-3 rounded-xl bg-red-950/30 border border-red-500/40 text-red-200 text-xs flex items-start gap-2" data-testid="rate-error-note">
          <AlertTriangle className="w-4 h-4 flex-shrink-0" />
          <span>{status.error_count} consultas al BCV fallaron seguidas. Se reintenta cada {status.schedule?.interval_minutes} min. Último error: {status.last_error}</span>
        </p>
      )}

      <div className="flex flex-wrap gap-2">
        <button onClick={() => run("check", api.checkExchangeRateNow, "Consulta al BCV realizada")}
          disabled={!!busy} data-testid="rate-check-now-btn"
          className="bg-cyan-600 hover:bg-cyan-500 disabled:opacity-40 text-white font-bold px-4 py-2.5 rounded-xl text-xs inline-flex items-center gap-2">
          {busy === "check" ? <RefreshCw className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Consultar BCV ahora
        </button>
        {manual && (
          <button onClick={() => run("auto", api.enableExchangeRateAuto, "Actualización automática reactivada")}
            disabled={!!busy} data-testid="rate-enable-auto-btn"
            className="bg-gradient-to-r from-emerald-600 to-cyan-600 hover:opacity-90 disabled:opacity-40 text-white font-bold px-4 py-2.5 rounded-xl text-xs inline-flex items-center gap-2">
            {busy === "auto" ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Reactivar actualización automática
          </button>
        )}
      </div>

      <div className="space-y-1.5">
        <p className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 flex items-center gap-1.5">
          <History className="w-3 h-3" /> Historial de cambios
        </p>
        <div className="space-y-1 max-h-64 overflow-y-auto" data-testid="rate-history-list">
          {(status.history || []).length === 0 && <p className="text-xs text-zinc-500">Sin cambios registrados aún.</p>}
          {status.history?.map((h, i) => (
            <div key={`${h.at}-${i}`} data-testid={`rate-history-item-${i}`}
              className="p-2.5 rounded-xl bg-ag7-bg border border-[var(--ag7-border-soft)] text-[11px] font-mono flex flex-wrap items-center justify-between gap-2">
              <span className="text-zinc-400">{fmtDate(h.at)}</span>
              <span className="text-white">
                {h.bcv_usd != null && <span className="text-zinc-500">BCV {Number(h.bcv_usd).toLocaleString("es-VE", { maximumFractionDigits: 4 })} → </span>}
                <strong>{fmtBs(h.rate)}</strong>
              </span>
              <span className={`px-2 py-0.5 rounded-full text-[9px] uppercase tracking-widest border ${
                h.source === "MANUAL" ? "text-amber-300 border-amber-500/40" : "text-emerald-300 border-emerald-500/40"}`}>
                {h.source}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
